import * as EventEmitter from 'eventemitter3';
import { FrameType } from './FrameType';
import { getBackgroundStyleFromImageURL, isUUID, newUUID } from '../utils';
import { trimImage } from '../utils/GraphicsUtils';
import { FrameData } from './FlyerData';
import BackgroundStyle from './BackgroundStyle';

const PIXI = (window as any).PIXI;
const URL = (window as any).URL || (window as any).webkitURL;

export class Frame extends EventEmitter {
  id: string = newUUID();

  isEditable = false;
  isResizable = false;
  hasBeenEdited = true;
  stageWidth = 0;
  stageHeight = 0;

  backgroundStyle = BackgroundStyle.Unknown;
  type: FrameType = FrameType.Unknown;
  texture: PIXI.Texture | null;
  // width = 0;
  // height = 0;
  isLoaded = false;
  isClone = false;
  relativeDuration = 1;

  sprite = new PIXI.picture.Sprite();
  protected _rotation: number = 0;
  get rotation(): number {
    return this._rotation;
  }
  set rotation(value: number) {
    this._rotation = value;
    if (this.sprite) {
      this.sprite.angle = value;
    }
  }

  get canRotate(): boolean {
    return false;
  }

  protected _boundingBox: PIXI.Rectangle;
  get boundingBox(): PIXI.Rectangle {
    return this._boundingBox;
  }
  set boundingBox(value: PIXI.Rectangle) {
    this._boundingBox = value;
    if (this.sprite) {
      this.sprite.x = value.x + value.width * 0.5;
      this.sprite.y = value.y + value.height * 0.5;
      this.sprite.width = value.width;
      this.sprite.height = value.height;
    }
  }

  private _thumbnailURL: string;
  private thumbResolvePromises: any[] = [];
  private typeResolvePromises: any[] = [];

  get isVideo(): boolean {
    return this.type === FrameType.Video;
  }

  constructor(stageWidth: number, stageHeight: number) {
    super();

    this.stageWidth = stageWidth;
    this.stageHeight = stageHeight;

    this.sprite.anchor.set(0.5);
  }

  removedFromView() {
    // override by subclass
  }

  populateWithData(data: FrameData) {
    // override by subclasses
  }

  asData(forDatabase: boolean): FrameData {
    const result: FrameData = {
      type: this.type
    };
    if (!isUUID(this.id)) {
      result.id = this.id;
    }
    if (this.relativeDuration !== 1) {
      result.relativeDuration = this.relativeDuration;
    }
    if (this.rotation !== 0) {
      result.rotation = this.rotation;
    }
    return result;
  }

  didUnselect() {
    // override by subclass
  }

  async waitForBaseTextureLoad() {
    return new Promise((resolve: Function, reject: Function) => {
      this.texture!.baseTexture.on('error', () => {
        reject();
      });
      this.texture!.baseTexture.on('loaded', () => {
        resolve();
      });
    });
  }

  async load() {
    throw new Error('Frame.load method should be overridden by the subclasses');
  }

  generateThumbnail(): void {
    this.generateThumbnailFromSource(this.texture!.baseTexture.source!);
  }

  generateThumbnailFromSource(source: any): void {
    if (this.thumbnailURL) {
      URL.revokeObjectURL(this.thumbnailURL);
    }
    const THUMBNAIL_SIZE: number = 120;
    const canvas = document.createElement('canvas');
    canvas.width = THUMBNAIL_SIZE;
    canvas.height = THUMBNAIL_SIZE;
    var ctx: CanvasRenderingContext2D = canvas.getContext('2d')!;

    if (this.type === FrameType.Image || this.type === FrameType.AnimatedGIF) {
      source = trimImage(source as HTMLImageElement);
    }

    const width = source.width;
    const height = source.height;

    const scale = THUMBNAIL_SIZE / Math.max(width, height);
    const w = width * scale;
    const h = height * scale;

    ctx.drawImage(
      source,
      (THUMBNAIL_SIZE - w) * 0.5,
      (THUMBNAIL_SIZE - h) * 0.5,
      w,
      h
    );
    this.thumbnailURL = canvas.toDataURL('image/png');
  }

  getThumbnailURL(): Promise<string> {
    // return Promise
    return new Promise(resolve => {
      if (this.thumbnailURL) {
        resolve(this.thumbnailURL);
      } else {
        this.thumbResolvePromises.push(resolve);
      }
    });
  }

  getBackgroundStylePromise(): Promise<BackgroundStyle> {
    return new Promise(resolve => {
      if (this.backgroundStyle !== BackgroundStyle.Unknown) {
        resolve(this.backgroundStyle);
      } else {
        this.typeResolvePromises.push(resolve);
      }
    });
  }

  clone(copyIndex: number): Frame {
    throw new Error(
      'Frame.clone method should be overridden by the subclasses'
    );
  }

  dispose() {
    //
  }

  protected get thumbnailURL(): string {
    return this._thumbnailURL;
  }
  protected set thumbnailURL(url: string) {
    this.thumbResolvePromises.forEach(resolve => {
      resolve(url);
    });
    this.thumbResolvePromises = [];
    if (this.backgroundStyle === BackgroundStyle.Unknown) {
      getBackgroundStyleFromImageURL(url).then(
        (backgroundStyle: BackgroundStyle) => {
          this.backgroundStyle = backgroundStyle;
          this.typeResolvePromises.forEach(resolve => {
            resolve(backgroundStyle);
          });
          this.typeResolvePromises = [];
        }
      );
    }
    this._thumbnailURL = url;
  }

  startEditing() {
    this.emit('start-editing');
  }
}



// WEBPACK FOOTER //
// ./src/data/Frame.ts